import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { addMilliseconds, addSeconds, isFuture } from 'date-fns';
import { FireState, IgnoreAutoFireMode } from 'enums/firing';
import { ResetPlateMode } from 'enums/machine.enums';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import {
  IActiveFireButton,
  IFireData,
  IFireResult,
  IFireStateDates,
  IIgnoreAutoFireProp,
} from 'interfaces/i-firing';
import { ISendBP, ISendTraining } from 'interfaces/i-pitch-aiming';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IFireEvent } from 'lib_ts/interfaces/i-session-event';
import { IFireResponseMsg } from 'lib_ts/interfaces/machine-msg/i-fire-response';
import { ITrainingMsg } from 'lib_ts/interfaces/machine-msg/i-training';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useMachineStatusStore from 'stores/use-machine-status-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const CONTEXT_NAME = 'FiringContext';

// after firing while training, how many seconds to wait before exiting waiting for the final training response
const TIMEOUT_TR_SEC = 20;

// after firing, how many seconds to wait before exiting waiting for any fire response
const TIMEOUT_FR_SEC = 10;

// after sending, how many seconds to wait before processing any r2f events
const IGNORE_R2F_SEC = 2;

// after ms hash changes, how many seconds to say we are loading for
const MIN_LOADING_SEC = 2;

export interface IFiringContext {
  fireState: FireState;
  readonly setFireState: (v: FireState) => void;

  // undefined if user is not active on the machine
  activeButton?: IActiveFireButton;

  readonly trySend: (
    from: string,
    data: ISendBP | ISendTraining
  ) => Promise<boolean>;
  // provide sendConfig to use while outside of training (e.g. queue context needs to change pitch and auto-send to machine)
  readonly tryFire: (from: string, data: IFireData) => Promise<IFireResult>;
  lastFire: IFireEvent | undefined;

  // for other components to force loading state w/o triggering r2f states
  // e.g. training dialog updates shots, re-aims, and auto-sends and button should go directly from any awaiting => loading w/o being r2f
  readonly setLoadingUntil: (date: Date) => void;

  ignoreAf: IgnoreAutoFireMode;
  // ignores auto-fire (e.g. after switching selected pitch) until next fire response
  readonly safeSetIgnoreAf: (v: IgnoreAutoFireMode) => void;

  // whether auto-firing AND ignoreAf is in disabled state
  willAutoFire: boolean;

  readonly clearFireDate: () => void;

  suspended: boolean;
  readonly setSuspended: (v: boolean) => void;
}

const DEFAULT: IFiringContext = {
  fireState: FireState.None,
  setFireState: () => console.error(`${CONTEXT_NAME}: not init`),

  activeButton: undefined,

  trySend: () =>
    new Promise(() => {
      console.error(`${CONTEXT_NAME}: not init`);
      return false;
    }),

  tryFire: () =>
    new Promise(() => {
      console.error(`${CONTEXT_NAME}: not init`);
      return false;
    }),

  lastFire: undefined,

  setLoadingUntil: () => console.error(`${CONTEXT_NAME}: not init`),

  ignoreAf: IgnoreAutoFireMode.Permanent,
  safeSetIgnoreAf: () => console.error(`${CONTEXT_NAME}: not init`),

  willAutoFire: false,

  clearFireDate: () => console.error(`${CONTEXT_NAME}: not init`),

  suspended: false,
  setSuspended: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const FiringContext = createContext(DEFAULT);

interface IProps extends IIgnoreAutoFireProp {
  children: ReactNode;
}

export const FiringProvider: FC<IProps & IVerboseComponent> = (props) => {
  const readyToTrain = useTrainedMsStore((s) => s.readyToTrain);

  const r2fReady = useMachineStatusStore((s) => s.r2fReady);
  const r2fDate = useMachineStatusStore((s) => s.r2fDate);
  const s2mReady = useMachineStatusStore((s) => s.s2mReady);
  const fireStateDates = useMachineStatusStore((s) => s.fireStateDates);

  const {
    isTraining,
    pitchBP,
    pitchTraining,
    lastChanged: lastSentFromAimingCx,
    setAndSendForBP,
    setAndSendForTraining,
  } = useContext(AimingContext);
  const latestSSBP = useLatest(setAndSendForBP);
  const latestSSTraining = useLatest(setAndSendForTraining);

  const {
    autoFire,
    lastBallCount,
    lastMSHash,
    fire: machineCxFire,
  } = useContext(MachineContext);

  const [ignoreAf, setIgnoreAf] = useState(DEFAULT.ignoreAf);

  const safeSetIgnoreAf = useCallback(
    (nextValue: IgnoreAutoFireMode) => {
      setIgnoreAf((prev) => {
        switch (nextValue) {
          case IgnoreAutoFireMode.Permanent: {
            // nothing blocks this
            verboseDebug(
              props,
              `Skipping set ignore AF to ${nextValue}; remaining on ${IgnoreAutoFireMode.Permanent}`
            );
            return IgnoreAutoFireMode.Permanent;
          }

          case IgnoreAutoFireMode.Disabled:
          case IgnoreAutoFireMode.Temporary: {
            if (prev === IgnoreAutoFireMode.Permanent) {
              // prevent leaving permanent ignore AF from external triggers
              return IgnoreAutoFireMode.Permanent;
            }

            verboseDebug(props, `Safely setting ignore AF to ${nextValue}`);
            return nextValue;
          }
        }
      });
    },
    [setIgnoreAf]
  );

  // whenever AF changes, ignore AF until the next fire response
  useEffect(() => {
    setIgnoreAf(IgnoreAutoFireMode.Permanent);
  }, [autoFire]);

  // auto-ignore auto-fire until reloaded properly and manually fired once
  useEffect(() => {
    if (lastBallCount !== 1) {
      setIgnoreAf(IgnoreAutoFireMode.Permanent);
    }
  }, [lastBallCount]);

  // only relevant during training, to differentiate between showing the fire button or waiting for data
  // after firing => set to current date
  // while training, reset fireDate after both FR and TR are final
  const [lastFireDate, setLastFireDate] = useState<Nullable<Date>>(null);

  // after firing while training, this gets set to some time in the future (for auto-reset if no TR is received again)
  // upon training response w/ success true or false (not undefined), this gets set to the current date
  const [awaitTrainingUntil, setAwaitTrainingUntil] = useState(new Date());

  // after firing, this gets set to some time in the future (for auto-reset if no FR is received again)
  // upon fire response, this gets set to the current date
  const [awaitFiringUntil, setAwaitFiringUntil] = useState(new Date());

  // after sending, this gets set to a couple seconds in the future
  const [ignoreR2fUntil, setIgnoreR2fUntil] = useState(new Date());

  // after firing, this gets set to some time in the future (for auto-reset if no R2F is received again)
  // upon r2f w/ overall status true, this gets set to the current date
  const [lastR2fDate, setLastR2fDate] = useState(new Date());

  const [loadingUntil, setLoadingUntil] = useState(new Date());

  // if suspended, don't react to firing state changes
  const [suspended, setSuspended] = useState(DEFAULT.suspended);

  useEffect(() => {
    if (suspended) {
      // don't update fire state dates if suspended
      return;
    }

    const o: IFireStateDates = {
      lastFireDate: lastFireDate,
      lastR2fDate: lastR2fDate,

      awaitTrainingUntil: awaitTrainingUntil,
      awaitFiringUntil: awaitFiringUntil,

      ignoreR2fUntil: ignoreR2fUntil,
      loadingUntil: loadingUntil,
    };

    useMachineStatusStore.setState({ fireStateDates: o });
  }, [
    lastFireDate, // from triggering a fire
    lastR2fDate, // from receiving R2F msgs
    awaitTrainingUntil, // set when firing
    awaitFiringUntil, // set when firing
    ignoreR2fUntil,
    loadingUntil,
    suspended,
  ]);

  useEffect(() => {
    // we can't trust a true R2F message until ignoreR2fUntil has passed
    if (r2fReady && isFuture(ignoreR2fUntil)) {
      verboseDebug(
        props,
        `ignored R2F > until ${ignoreR2fUntil.toISOString()}`
      );
      return;
    }

    // we can now trust this
    if (r2fReady) {
      setLastR2fDate(r2fDate ?? new Date());
      return;
    }

    // not ready => punt this 5 seconds into the future - we should receive another R2F message before then
    setLastR2fDate(addSeconds(new Date(), 5));
  }, [ignoreR2fUntil, r2fReady, r2fDate, setLastR2fDate, loadingUntil]);

  // listen to fire response messages
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: IFireResponseMsg = event.detail;

      setAwaitFiringUntil(new Date());

      if (!data.status) {
        return;
      }

      // stop ignoring AF once we've received a successful FR (i.e. user has fired manually 1x)
      setIgnoreAf(IgnoreAutoFireMode.Disabled);
    };

    WebSocketHelper.on(WsMsgType.M2U_FireResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_FireResponse, callback);
    };
  }, [setAwaitFiringUntil]);

  // training response, for debug purposes
  const [tr, setTR] = useState<ITrainingMsg>();

  // listen to training response messages only while training
  useEffect(() => {
    if (!isTraining) {
      return;
    }

    const callback = (event: CustomEvent) => {
      const data: ITrainingMsg = event.detail;
      setTR(data);

      // a TR with undefined success value reports interim results from each tracking device
      // a TR with true or false (not undefined) success value is final
      if (data.success !== undefined) {
        // verboseDebug(props,
        //   `${CONTEXT_NAME} received (final) training response:\n`,
        //   JSON.stringify(data, null, 2)
        // );

        if (props.ignoreAfOnTr) {
          if (data.success) {
            safeSetIgnoreAf(IgnoreAutoFireMode.Temporary);
          }

          // * set lastTrDate slightly into the future to allow for the temporary ignore AF to take effect
          setAwaitTrainingUntil(addMilliseconds(new Date(), 100));
          return;
        }

        setAwaitTrainingUntil(new Date());
      }
    };

    WebSocketHelper.on(WsMsgType.M2U_TrainingResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_TrainingResponse, callback);
    };
  }, [
    props.ignoreAfOnTr,
    safeSetIgnoreAf,
    isTraining,
    setTR,
    setAwaitTrainingUntil,
  ]);

  // show a toast if the final TR says we should
  useEffect(() => {
    if (!tr) {
      return;
    }

    const { timeout: timeoutInstructions } = tr;

    if (!timeoutInstructions) {
      // * server (currently) only sets the timeout property for a TR with success false
      // * this means we should never get past this for a non-final TR
      return;
    }

    const msg = (() => {
      switch (timeoutInstructions.missing) {
        case 'shot': {
          return t('tr.training-timeout-no-shot-msg', {
            sec: timeoutInstructions.delay_s,
          });
        }

        case 'MSBS':
        case TrackingDevice.RapsodoV3PRO:
        case TrackingDevice.TrackmanB1:
        case TrackingDevice.TrajektVision:
        default: {
          return t('tr.training-timeout-missing-msg', {
            sec: timeoutInstructions.delay_s,
            missing: timeoutInstructions.missing,
          });
        }
      }
    })();

    NotifyHelper.warning({
      message_md: msg,
      inbox: true,
      buttons: [
        {
          label: 'common.read-more',
          onClick: () =>
            window.open(t('common.intercom-url') + HELP_URLS.RAPSODO_ERRORS),
        },
      ],
    });
  }, [tr]);

  // update loading until whenever a new ms is sent to the machine
  useEffect(() => {
    if (!lastMSHash) {
      return;
    }

    const until = addSeconds(new Date(), MIN_LOADING_SEC);

    verboseDebug(props, `setLoadingUntil > internal > ${until.toISOString()}`);
    setLoadingUntil(until);
  }, [lastMSHash]);

  // only subscribes to heartbeat to avoid unnecessary rerenders
  const [fireState, setFireState] = useState(DEFAULT.fireState);

  const isAnyPitchSelected = !!pitchTraining || !!pitchBP;

  const activeButton = useMemo<IActiveFireButton | undefined>(() => {
    const output: IActiveFireButton = {
      state: fireState,
      label: fireState,
      disabled: false,
    };

    switch (fireState) {
      case FireState.AwaitingFR:
      case FireState.Loading:
      case FireState.NotReadyToFire: {
        output.label = 'common.loading';
        output.description = 'common.preparing-to-fire';
        output.color = RADIX.COLOR.NEUTRAL;
        output.disabled = true;
        output.spinning = true;
        break;
      }

      // todo: this should not be necessary if there's always a snapshot; otherwise this could trigger a (first) build
      // e.g. changed ball types after nightly snapshot job
      case FireState.UncalibratedOD: {
        output.label = 'TODO: Calibration Shots';
        output.color = RADIX.COLOR.TRAIN_PITCH;
        break;
      }

      case FireState.UntrainedBP: {
        output.label = 'common.train-pitch';
        output.color = RADIX.COLOR.TRAIN_PITCH;
        output.disabled = !s2mReady || !readyToTrain;
        break;
      }

      case FireState.AwaitingTR: {
        output.label = 'common.waiting-for-data';
        output.description = 'common.collecting-training-data';
        output.color = RADIX.COLOR.NEUTRAL;
        output.disabled = true;
        output.spinning = true;
        break;
      }

      case FireState.BallError: {
        output.label = 'Ball Error';
        output.color = RADIX.COLOR.WARNING;
        break;
      }

      case FireState.DropBall: {
        output.label = 'main.drop-ball';
        output.color = RADIX.COLOR.WARNING;
        break;
      }

      case FireState.LoadPitch: {
        output.label = 'common.load-pitch';
        output.color = RADIX.COLOR.SEND_PITCH;

        const isStillFiring = isFuture(fireStateDates.awaitFiringUntil);

        // * clicking this permanently ignores auto-fire, so don't offer it while auto-fire is sending
        const autoFireOwnsLoop =
          autoFire && ignoreAf !== IgnoreAutoFireMode.Permanent;

        output.disabled =
          isStillFiring || !isAnyPitchSelected || !s2mReady || autoFireOwnsLoop;
        break;
      }

      case FireState.Firing:
      case FireState.ReadyToAutoFire: {
        output.label = 'common.firing';
        output.color = RADIX.COLOR.FIRE_PITCH;
        output.disabled = true;
        output.spinning = true;
        break;
      }

      case FireState.ReadyToManualFire: {
        output.label = 'common.fire';
        output.description = 'common.fire';
        output.color = RADIX.COLOR.FIRE_PITCH;
        output.variant = 'solid';
        break;
      }

      case FireState.Paused: {
        output.label = 'common.paused';
        output.color = RADIX.COLOR.NEUTRAL;
        output.disabled = true;
        break;
      }

      case FireState.Busy: {
        output.label = 'common.busy';
        output.color = RADIX.COLOR.WARNING;
        break;
      }

      case FireState.OfflineMachine: {
        output.label = 'common.machine-disconnected';
        output.color = RADIX.COLOR.DANGER;
        break;
      }

      case FireState.OfflineUser: {
        output.label = 'common.user-disconnected';
        output.color = RADIX.COLOR.DANGER;
        break;
      }

      case FireState.None: {
        output.label = 'common.loading';
        output.disabled = true;
        output.spinning = true;
        break;
      }

      default: {
        // don't allow use of a button that's not defined
        output.disabled = true;
        output.color = 'pink';
        output.variant = 'solid';
        break;
      }
    }

    return output;
  }, [
    s2mReady,
    readyToTrain,
    fireState,
    fireStateDates.awaitFiringUntil,
    isAnyPitchSelected,
    autoFire,
    ignoreAf,
  ]);

  // whenever aiming context successfully sends to machine, auto-ignore all R2F for a brief period
  useEffect(() => {
    setIgnoreR2fUntil(addSeconds(new Date(), IGNORE_R2F_SEC));
  }, [lastSentFromAimingCx]);

  const trySend = useCallback(
    async (from: string, data: ISendBP | ISendTraining) => {
      try {
        verboseDebug(props, `trySend > ${from} > triggered`);

        switch (isTraining) {
          case true: {
            verboseDebug(props, `trySend > ${from} > training`);

            await latestSSTraining.current({
              pitch: pitchTraining,
              sendConfig: data as ISendTraining,
            });

            return true;
          }

          case false: {
            verboseDebug(props, `trySend > ${from} > BP`);

            await latestSSBP.current({
              pitch: pitchBP,
              resetPlate: ResetPlateMode.Skip,
              sendConfig: data as ISendBP,
            });

            return true;
          }

          default: {
            return false;
          }
        }
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    [isTraining, pitchTraining, pitchBP]
  );

  const [lastFire, setLastFire] = useState<IFireEvent>();

  const tryFire = useCallback(
    async (from: string, data: IFireData): Promise<IFireResult> => {
      try {
        verboseDebug(props, `tryFire > ${from} > triggered`);

        const date = new Date();
        setLastFireDate(date);

        // if successful
        setIgnoreR2fUntil(addSeconds(date, IGNORE_R2F_SEC));

        // if successful
        setAwaitFiringUntil(addSeconds(date, TIMEOUT_FR_SEC));

        // if successful AND training
        if (isTraining) {
          setAwaitTrainingUntil(addSeconds(date, TIMEOUT_TR_SEC));
        }

        if (isTraining) {
          if (!pitchTraining) {
            verboseDebug(props, `tryFire > ${from} > no training pitch`);
            return { success: false };
          }

          const result = await machineCxFire({
            pitch: pitchTraining,
            fireData: data,
          });

          setLastFire(result.event);

          return result;
        }

        if (!pitchBP) {
          verboseDebug(props, `tryFire > ${from} > no BP pitch`);
          return { success: false };
        }

        const result = await machineCxFire({
          pitch: pitchBP,
          fireData: data,
        });

        setLastFire(result.event);

        return result;
      } catch (e) {
        console.error(e);
        return { success: false };
      }
    },
    [isTraining, pitchBP, pitchTraining, machineCxFire, setLastFire]
  );

  const state: IFiringContext = {
    fireState: fireState,
    setFireState: setFireState,

    activeButton: activeButton,

    trySend: trySend,

    tryFire: tryFire,
    lastFire: lastFire,

    setLoadingUntil: (date) => {
      verboseDebug(props, `setLoadingUntil > external > ${date.toISOString()}`);
      setLoadingUntil(date);
    },

    ignoreAf: ignoreAf,
    safeSetIgnoreAf: safeSetIgnoreAf,

    willAutoFire: autoFire && ignoreAf === IgnoreAutoFireMode.Disabled,

    clearFireDate: () => setLastFireDate(null),

    suspended: suspended,
    setSuspended: (v) => {
      if (v) {
        // automatically start ignoring AF whenever becoming suspended for safety
        safeSetIgnoreAf(IgnoreAutoFireMode.Temporary);
      }

      setSuspended(v);
    },
  };

  return (
    <FiringContext.Provider value={state}>
      {props.children}
    </FiringContext.Provider>
  );
};
